import { useState } from "react";
import { formatDateOnly } from "@/lib/date-only";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { apiGet } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  AlertTriangle,
  Plus,
  ArrowRight,
  Filter,
} from "lucide-react";

/**
 * Phase 5: hazard reports list.
 *
 * This is a new top-level route specifically for the "Report Hazard"
 * flow. Under the hood it reads from the existing `/api/incidents`
 * endpoint (filtered to `incidentType=near_miss`). The full incident
 * detail page at `/incidents/$incidentId` is reused — tapping a card
 * navigates there.
 */
export const Route = createFileRoute("/_authenticated/hazards")({
  component: HazardsListPage,
});

interface HazardSummary {
  id: string;
  incidentType: string;
  severity: string;
  date: string;
  location: string | null;
  description: string;
  status: string;
  reportedByName: string | null;
  createdAt: string;
}

const STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "reported", label: "Open" },
  { value: "under_investigation", label: "Investigating" },
  { value: "closed", label: "Closed" },
] as const;

const SEVERITY_FILTERS = [
  { value: "all", label: "All severity" },
  { value: "minor", label: "Low" },
  { value: "moderate", label: "Medium" },
  { value: "serious", label: "High" },
  { value: "critical", label: "Critical" },
] as const;

const SEVERITY_COLORS: Record<string, string> = {
  minor: "bg-slate-100 text-slate-800",
  moderate: "bg-amber-100 text-amber-800",
  serious: "bg-orange-100 text-orange-800",
  critical: "bg-red-100 text-red-800",
};

const SEVERITY_LABELS: Record<string, string> = {
  minor: "Low",
  moderate: "Medium",
  serious: "High",
  critical: "Critical",
};

function HazardsListPage() {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [severityFilter, setSeverityFilter] = useState<string>("all");

  const { data, isLoading } = useQuery({
    queryKey: ["hazards", statusFilter, severityFilter],
    queryFn: () => {
      const params = new URLSearchParams({
        incidentType: "near_miss",
        status: statusFilter,
        severity: severityFilter,
        limit: "50",
      });
      return apiGet<{ data: HazardSummary[]; total: number }>(
        `/api/incidents?${params.toString()}`
      );
    },
  });

  const hazards = data?.data || [];

  return (
    <div className="space-y-4 max-w-lg mx-auto pb-24">
      <div className="flex items-center justify-between pt-2">
        <h1 className="text-2xl font-bold text-text-primary">Hazards</h1>
        <Link to="/hazards/new">
          <Button size="sm" className="bg-paris-red hover:bg-paris-red-dark gap-1">
            <Plus className="h-4 w-4" />
            Report
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 scrollbar-hide">
          <Filter className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatusFilter(f.value)}
              className={cn(
                "text-xs px-2.5 py-1 rounded-full border whitespace-nowrap transition-colors",
                statusFilter === f.value
                  ? "bg-pph-charcoal text-white border-pph-charcoal"
                  : "bg-background border-input text-muted-foreground hover:bg-accent"
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 scrollbar-hide">
          <AlertTriangle className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          {SEVERITY_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setSeverityFilter(f.value)}
              className={cn(
                "text-xs px-2.5 py-1 rounded-full border whitespace-nowrap transition-colors",
                severityFilter === f.value
                  ? "bg-pph-charcoal text-white border-pph-charcoal"
                  : "bg-background border-input text-muted-foreground hover:bg-accent"
              )}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      )}

      {!isLoading && hazards.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <AlertTriangle className="h-12 w-12 text-muted-foreground/40 mb-3" />
            <p className="text-lg font-medium text-muted-foreground">
              No hazards reported
            </p>
            <p className="text-sm text-muted-foreground/70 mt-1 max-w-xs">
              Spot something risky on site? Tap "Report" to capture it in seconds.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && hazards.length > 0 && (
        <div className="space-y-2">
          {hazards.map((hazard) => (
            <Link
              key={hazard.id}
              to="/incidents/$incidentId"
              params={{ incidentId: hazard.id }}
              className="block"
            >
              <Card className="cursor-pointer active:bg-muted/30 transition-colors">
                <CardContent className="py-3 px-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap mb-1">
                        <Badge className={cn("text-xs px-1.5 py-0", SEVERITY_COLORS[hazard.severity] || "bg-slate-100")}>
                          {SEVERITY_LABELS[hazard.severity] ?? hazard.severity}
                        </Badge>
                        <Badge variant="outline" className="text-xs px-1.5 py-0">
                          {hazard.status.replace(/_/g, " ")}
                        </Badge>
                        <span className="text-[11px] text-muted-foreground">
                          {formatDateOnly(hazard.date, "en-CA")}
                        </span>
                      </div>
                      <p className="text-sm line-clamp-2 text-text-primary">
                        {hazard.description}
                      </p>
                      {hazard.reportedByName && (
                        <p className="text-[11px] text-muted-foreground mt-1">
                          by {hazard.reportedByName}
                        </p>
                      )}
                    </div>
                    <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
